<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!tableData && !showLoader && !applyErrorState)"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :errorStateMessage="t('accounts.visible.emptyState.error')"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Visible Filters -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFilters"
        @removeFilterTag="closeAllFiltersHandler"
        @closeAllFilters="closeAllFiltersHandler">
            <!-- Account Types Dropdown -->
            <dropdown-generic
            :params="accountTypesDropdownParams"
            :options="accountTypesDropdownOptions"
            :selectedOption="filters.selectedAccountType"
            @clicked="filters.selectedAccountType = $event"/>
        </consult-filters-wrapper>
        <!-- Table -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <!-- Yes, this v-if can be applied directly in component, but for magic reason, if it's there, the transition only triggers the first time -->
            <div
            v-if="tableData && !showLoader && !applyErrorState"
            class="transition-helper-element prevent-auto-margins"
            :key="`tableDataChanged${JSON.stringify(filters.value)}`">
                <table-generic
                applyHeaderForMobile
                :headerData="tableHeaderData"
                :tableData="tableData"
                :tableStyles="tableStyles"
                :hasDetail="false"
                @selectedRadio="radioClickHandler($event)"
                @toggleSwitch="[switchClickHandler($event), parseToSelectRowSwitchHandler()]"/>
            </div>
        </transition>
        <template #empty-state-slot>
            <!-- Empty State Generic -->
            <empty-state-generic
            v-if="!tableData && !showLoader && !applyErrorState"
            illustration="monocrome/icon-transactions"
            :title="t('accounts.visible.emptyState.genericTitle')"/>
        </template>
	</wrapper-features-templates-one-column>

    <!-- Modal: Switch-->
    <modal-centered-generic
    v-if="showModalSwitch"
    titleIcon="info"
    rightButtonIcon="check-line"
    :title="t('accounts.visible.modalSwitch.title')"
    :text="t('accounts.visible.modalSwitch.text', { accountName: accountNameModalSwitch, isVisible: isVisibleTextModalSwitch} )"
    :rightButtonText="rightButtonTextModalSwitch"
    :leftButtonText="t('accounts.visible.modalSwitch.leftButtonText')"
    @hideMe="[showModalSwitch = false, parseToSelectRowSwitchHandler()]"
    @rightButtonClicked="rightButtonModalSwitch"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Aux */
import { ACCOUNTS_NIB_IBAN, WALLET } from '@/assets/constants/masks'
import { currencyParser } from '@/assets/scripts/amounts'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { parseToSelectRowSwitch } from '@/assets/scripts/parsers/tables'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { getMobileAppState } = mapGetters()
const {	accountsListFetch, changeDefaultAccount, changeAccountVisibility } = mapActions(moduleMapper.ACCOUNTS_VISIBLE)
const { getAccountsList, getAccountsTypesList } = mapGetters(moduleMapper.ACCOUNTS_VISIBLE)

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showModalSwitch = ref(false)
const showFilters = ref(false)

/* Account List Fetch */
const payloadAccountsList = {
    params: {
        showAll: true
    }
}

/*************************************************
*                                                *
*                     Table                      *
*                                                *
*************************************************/
const tableHeaderData = [
	{ label: t('accounts.visible.tableHeaders.accountNumber'), showInMobile: { groupWith: 'balance' }, rowCellType: 'Title', dataAttr: 'accountNumber' },
    { label: t('accounts.visible.tableHeaders.description'), rowCellType: 'description', dataAttr: 'description' },
    { label: t('accounts.visible.tableHeaders.accountType'), dataAttr: 'accountType' },
	{ label: t('accounts.visible.tableHeaders.balance'), rowCellType: 'amount', dataAttr: 'balance' },
    { label: t('accounts.visible.tableHeaders.currency'), rowCellType: 'currency', dataAttr: 'currency' },
    { label: t('accounts.visible.tableHeaders.status'), showInMobile: true, rowCellType: 'switch', dataAttr: 'switch' },
    { label: t('accounts.visible.tableHeaders.omission'), showInMobile: true, rowCellType: 'radio', dataAttr: 'radio' }
]

const tableData = ref()

const tableStyles = [
    getMobileAppState.value ? null : { width: '170px', 'min-width': '170px' },
    null,
    { width: '85px', 'min-width': '85px' },
    { 'text-align': 'end' },
    { width: '60px', 'min-width': '60px' },
    null,
    { width: '70px', 'min-width': '70px' }
]

/* Radio Table */
const radioClickHandler = async $event => {
    showOverlayLoader.value = true

    const payloadChangeDefault = { id: $event.rowId }

    try {
        const response = await changeDefaultAccount(payloadChangeDefault)
        await accountsListFetch(payloadAccountsList)
        setTableData()

        showOverlayLoader.value = false
        radioSuccessHandler(response.data)
    } catch (error) {
        if (error.toString() === 'Cancel') return
        showOverlayLoader.value = false
        errorHandler(error.data)
    }
}

/* Switch Table */
const accountNameModalSwitch = ref()
const isVisibleTextModalSwitch = ref()
const rightButtonTextModalSwitch = ref()
const eventRow = ref()
const eventIsActive = ref()

const selectedRowIsActive = ref()

const switchClickHandler = $event => {
    const accountNameSelected = getAccountsList.value.find(el => el.id === $event.rowId).description
    const isHidden = t('accounts.visible.modalSwitch.isHiddenText')
    const isVisible = t('accounts.visible.modalSwitch.isVisibleText')
    const isHiddenRightButton = t('accounts.visible.modalSwitch.rightButtonTextHidden')
    const isVisibleRightButton = t('accounts.visible.modalSwitch.rightButtonTextVisible')

    eventRow.value = $event.rowId
    eventIsActive.value = $event.isActive
    selectedRowIsActive.value = getAccountsList.value.find(el => el.id === $event.rowId).isVisible

    showModalSwitch.value = true
    accountNameModalSwitch.value = accountNameSelected
    isVisibleTextModalSwitch.value = $event.isActive ? isHidden : isVisible
    rightButtonTextModalSwitch.value = $event.isActive ? isHiddenRightButton : isVisibleRightButton
}

const parseToSelectRowSwitchHandler = $event => {
	tableData.value = parseToSelectRowSwitch(tableData.value, eventRow.value)
}

const rightButtonModalSwitch = async () => {
    const payloadChangeAccountVisibility = {
        params: {
            accountVisibility: {
                [eventRow.value]: !eventIsActive.value
            }
        }
    }

    try {
        showModalSwitch.value = false
        showOverlayLoader.value = true

        const response = await changeAccountVisibility(payloadChangeAccountVisibility)
        setTableData()

        showOverlayLoader.value = false
        switchSuccessHandler(response.data, eventRow.value)
    } catch (error) {
        showOverlayLoader.value = false
        if (error.toString() === 'Cancel') return
        errorHandler(error.data)
    }
}

/*************************************************
*                                                *
*                     Filters                    *
*                                                *
*************************************************/
const filters = ref({
    selectedAccountType: ''
})
const filterTags = ref([])

const accountTypesDropdownParams = ref({
	labelText: t('accounts.visible.filters.accountTypes.labelText'),
    mobileTitle: t('accounts.visible.filters.accountTypes.labelText'),
    placeholder: t('accounts.visible.filters.accountTypes.placeholder')
})

const accountTypesDropdownOptions = computed(() => {
    return getAccountsTypesList.value.map(accountType => {
        return {
            id: accountType.id,
            text: accountType.description
        }
    })
})

const applyButtonDisabled = computed(() => {
    return !filters.value.selectedAccountType.length || !!filterTags.value.find(tag => tag.value === filters.value.selectedAccountType)
})

const applyFilters = () => {
    const appliedFilters = filters.value
    filterTags.value = []

    for (const filter in appliedFilters) {
        if (appliedFilters[filter] && appliedFilters[filter].length) {
            filterTags.value.push({
                id: filter,
                label: t('accounts.visible.filterTag.label'),
                text: getAccountsTypesList.value.find(obj => obj.id === appliedFilters[filter]).description,
                value: appliedFilters[filter]
            })
        }
    }

    setTableData()
}

const closeAllFiltersHandler = () => {
    filterTags.value = []
    filters.value.selectedAccountType = ''
    setTableData()
}

/*************************************************
*                  Toasts Logic                  *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const switchSuccessHandler = (toast, accountId) => {
    const isVisible = toast.data.accountVisibility[accountId]
    const message = isVisible ? t('accounts.visible.successToast.visible') : t('accounts.visible.successToast.hidden')

    feedbackToastData.value = sucessObjectConstrutor(message)
    showFeedbackToast.value = true
}

const radioSuccessHandler = (response) => {
    feedbackToastData.value = sucessObjectConstrutor(response.message)
    showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

/*************************************************
*                                                *
*                Initial Requests                *
*                                                *
*************************************************/
const initialRequests = async payload => {
	showLoader.value = true
    applyErrorState.value = false
    showFilters.value = false

	try {
        await accountsListFetch(payloadAccountsList)
        setTableData()
        if (!getAccountsList.value.length) {
            applyErrorState.value = true
            errorHandler(null, initialRequests)
        } else {
            showFilters.value = true
        }
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response, initialRequests)
	} finally {
		showLoader.value = false
    }
}

const setTableData = () => {
    if (!getAccountsList.value) return []

    tableData.value = getAccountsList.value.reduce((acc, account) => {
        if (filterTags.value.length && account.accountType !== filterTags.value[0].value) return acc

        acc.push([
            { value: account.id, dataAttr: 'id' },
            { type: 'title', value: { content: account.accountNumber, mask: account.accountType === 'DWALLET' ? WALLET : ACCOUNTS_NIB_IBAN }, dataAttr: 'accountNumber' },
            { type: 'description', value: account.description, dataAttr: 'description' },
            { type: 'description', value: account.accountType, dataAttr: 'accountType' },
            { type: 'amount', value: { content: account.balance, currency: getMobileAppState.value === true ? currencyParser(account.currency) : null }, dataAttr: 'balance' },
            { type: 'description', value: currencyParser(account.currency), dataAttr: 'currency' },
            { type: 'switch', value: { isActive: account.isVisible, disabled: account.default }, dataAttr: 'switch' },
            { type: 'radio', value: { selected: account.default, disabled: account.isVisible === false, hideMe: !account.canBeUsedAsDefault }, dataAttr: 'radio' }
        ])

        return acc
    }, [])
}

initialRequests()
</script>

<style lang="scss" scoped>
.dropdown-wrapper {
    width: 312px;
    max-width: 312px;
}

.transition-helper-element {
    @include commonEnteringAnimation;
    width: 100%;
}
</style>
